import React, { useState } from 'react'
import { Link } from 'react-router'
import { useLocale, Card, Button, Spinner, Badge } from '@betina/ui'
import { useQuery, useMutation } from '../hooks/useApi.js'
import { mealPlanApi, dishLogApi, stockApi, aiApi } from '../lib/api.js'

function getThisMonday() {
  const d = new Date()
  const day = d.getDay()
  const diff = d.getDate() - day + (day === 0 ? -6 : 1)
  d.setDate(diff)
  return d.toISOString().slice(0, 10)
}

const DAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim']
const DAYS_EN = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const MEAL_TYPES = ['breakfast', 'lunch', 'dinner']

export function Dashboard() {
  const { t, locale } = useLocale()
  const weekStart = getThisMonday()
  const today = new Date().getDay() === 0 ? 6 : new Date().getDay() - 1 // 0=Mon

  const { data: plan, loading: planLoading, refetch: refetchPlan } = useQuery(
    () => mealPlanApi.get(weekStart),
    [weekStart]
  )

  const { data: logs, loading: logsLoading } = useQuery(
    () => dishLogApi.list(5),
    []
  )

  const { data: expiringItems } = useQuery(
    () => stockApi.list(),
    []
  )

  const { mutate: suggestMeals, loading: suggesting } = useMutation((data) =>
    aiApi.suggestMeals(data)
  )

  const [aiSuggestion, setAiSuggestion] = useState(null)

  const todaySlots = plan?.slots?.filter((s) => s.day_of_week === today) ?? []

  // Expiring items (within 3 days)
  const soon = (expiringItems ?? []).filter((i) => {
    if (!i.expiry_date) return false
    const days = Math.ceil((new Date(i.expiry_date) - new Date()) / 86400000)
    return days >= 0 && days <= 3
  })

  async function handleAISuggest() {
    const res = await suggestMeals({ weekStart, constraints: ['lactose-free'] })
    setAiSuggestion(res?.plan)
  }

  const dayLabels = locale === 'fr' ? DAYS : DAYS_EN

  return (
    <div className="p-4 space-y-4">
      {/* Today's meals */}
      <section>
        <h2 className="text-lg font-semibold text-gray-800 mb-3">
          {t('today')} — {dayLabels[today]}
        </h2>
        {planLoading ? (
          <div className="flex justify-center py-8"><Spinner /></div>
        ) : todaySlots.length > 0 ? (
          <div className="space-y-2">
            {MEAL_TYPES.map((type) => {
              const slot = todaySlots.find((s) => s.meal_type === type)
              if (!slot) return null
              return (
                <Card key={type}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wide">{t(type)}</p>
                      <p className="font-medium text-gray-800">{slot.custom_meal || 'Repas planifié'}</p>
                    </div>
                    <Badge color={slot.status === 'prepared' ? 'green' : slot.status === 'skipped' ? 'red' : 'gray'}>
                      {t(slot.status)}
                    </Badge>
                  </div>
                </Card>
              )
            })}
          </div>
        ) : (
          <Card className="text-center py-6">
            <p className="text-gray-400 text-sm mb-3">Aucun repas planifié aujourd'hui</p>
            <Link to="/planner">
              <Button size="sm">{t('planner')} →</Button>
            </Link>
          </Card>
        )}
      </section>

      {/* Expiry alerts */}
      {soon.length > 0 && (
        <section>
          <h3 className="text-sm font-semibold text-orange-600 mb-2">⚠️ {t('expires_soon')}</h3>
          <div className="flex flex-wrap gap-2">
            {soon.map((item) => (
              <Badge key={item.id} color="yellow">{item.name}</Badge>
            ))}
          </div>
        </section>
      )}

      {/* Quick actions */}
      <section>
        <h3 className="text-sm font-semibold text-gray-600 mb-2">Actions rapides</h3>
        <div className="grid grid-cols-2 gap-2">
          <Link to="/stock">
            <Card className="text-center py-4 hover:shadow-md transition-shadow">
              <div className="text-2xl mb-1">🧊</div>
              <p className="text-xs text-gray-600">{t('scan_fridge')}</p>
            </Card>
          </Link>
          <Link to="/shopping">
            <Card className="text-center py-4 hover:shadow-md transition-shadow">
              <div className="text-2xl mb-1">🛒</div>
              <p className="text-xs text-gray-600">{t('shopping_list')}</p>
            </Card>
          </Link>
          <Link to="/planner">
            <Card className="text-center py-4 hover:shadow-md transition-shadow">
              <div className="text-2xl mb-1">📅</div>
              <p className="text-xs text-gray-600">{t('planner')}</p>
            </Card>
          </Link>
          <button onClick={handleAISuggest} disabled={suggesting}>
            <Card className="text-center py-4 hover:shadow-md transition-shadow w-full">
              <div className="text-2xl mb-1">{suggesting ? '⏳' : '✨'}</div>
              <p className="text-xs text-gray-600">{t('ai_suggest')}</p>
            </Card>
          </button>
        </div>
      </section>

      {/* AI suggestions modal-like */}
      {aiSuggestion && (
        <section>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-emerald-700">✨ {t('suggestions')} IA</h3>
            <button onClick={() => setAiSuggestion(null)} className="text-gray-400 hover:text-gray-600 text-xs">✕ Fermer</button>
          </div>
          <Card>
            {aiSuggestion.days?.slice(0, 3).map((day) => (
              <div key={day.dayOfWeek} className="mb-3">
                <p className="font-medium text-sm text-gray-700">{day.dayName}</p>
                <div className="text-xs text-gray-500 space-y-0.5 ml-2">
                  {Object.entries(day.meals ?? {}).map(([type, meal]) => (
                    <p key={type}><span className="capitalize">{type}:</span> {meal?.name}</p>
                  ))}
                </div>
              </div>
            ))}
            <Link to="/planner">
              <Button size="sm" className="mt-2 w-full justify-center">Voir le planning complet →</Button>
            </Link>
          </Card>
        </section>
      )}

      {/* Recent dishes */}
      {!logsLoading && logs?.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-600">{t('history')}</h3>
            <Link to="/history" className="text-xs text-emerald-600">Voir tout →</Link>
          </div>
          <div className="space-y-1">
            {logs.slice(0, 3).map((log) => (
              <div key={log.id} className="flex items-center gap-2 text-sm py-1 border-b border-gray-100">
                <span className="text-base">🍽️</span>
                <span className="text-gray-700 flex-1">{log.name}</span>
                <span className="text-xs text-gray-400">
                  {new Date(log.prepared_at * 1000).toLocaleDateString(locale)}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  )
}
